/**
 * StudioTopicSuggestions — AI-powered topic recommendations panel.
 * Analyses channel's existing videos (titles + niche + keywords) and
 * generates the next batch of topics to produce. Lets you push directly
 * into the content pipeline as Ideas.
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Sparkles, Wand2, Loader2, Plus, RefreshCw, ChevronRight, AlertTriangle,
  CheckCircle2, ArrowLeft, TrendingUp, Flame,
} from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import "./studio.css";

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function StudioTopicSuggestions() {
  const [params] = useSearchParams();
  const channelIdFromQs = params.get("channel_id") || "";
  const [channels, setChannels] = useState([]);
  const [activeId, setActiveId] = useState(channelIdFromQs);
  const [topics, setTopics] = useState([]);
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState({});
  const [error, setError] = useState(null);
  const [trending, setTrending] = useState(null);
  const [trendBusy, setTrendBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/api/studio/channels`, { withCredentials: true })
      .then((r) => {
        if (!alive) return;
        const all = [...(r.data?.owned || []), ...(r.data?.shared || [])];
        setChannels(all);
        if (!activeId && all[0]) setActiveId(all[0].channel_id);
      })
      .catch((err) => alive && setError(err?.response?.data?.detail || err.message));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generate = async () => {
    if (!activeId) { toast.error("Pick a channel first"); return; }
    setBusy(true);
    setError(null);
    try {
      const r = await axios.post(
        `${API}/api/studio/ai/topic-suggestions`,
        { channel_id: activeId, count: 20 },
        { withCredentials: true }
      );
      setTopics(r.data.topics || []);
      setMeta({ niche: r.data.niche, based_on: r.data.based_on_titles });
      toast.success(`Generated ${r.data.topics?.length || 0} topics for "${r.data.niche}"`);
    } catch (err) {
      const det = err?.response?.data?.detail;
      // Structured "niche_missing" error: surface the fix link.
      if (det && typeof det === "object" && det.error === "niche_missing") {
        setError({
          niche_missing: true,
          message: det.message,
          fix_url: det.fix_url,
          channel_title: det.channel_title,
        });
        toast.error("Niche not set — random topics avoided");
      } else {
        const msg = typeof det === "string" ? det : (det?.message || err?.message || "AI request failed");
        setError(msg);
        toast.error(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  const addToPipeline = async (t, idx) => {
    setAdding((s) => ({ ...s, [idx]: true }));
    try {
      await axios.post(
        `${API}/api/studio/ai/topic-suggestions/add-to-pipeline`,
        { channel_id: activeId, ...t },
        { withCredentials: true }
      );
      toast.success(`Added "${t.title.slice(0, 40)}…" to pipeline`);
      setAdding((s) => ({ ...s, [idx]: "done" }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add");
      setAdding((s) => ({ ...s, [idx]: false }));
    }
  };

  const fetchTrending = async () => {
    if (!activeId) { toast.error("Pick a channel first"); return; }
    setTrendBusy(true);
    try {
      const r = await axios.get(
        `${API}/api/studio/channels/${activeId}/trending-keywords`,
        { withCredentials: true }
      );
      setTrending(r.data);
      toast.success(`${r.data.keywords?.length || 0} trending keywords found`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load trends");
    } finally {
      setTrendBusy(false);
    }
  };

  const activeChannel = channels.find((c) => c.channel_id === activeId);

  return (
    <>
      <Header />
      <div className="studio-shell">
        <div className="studio-hero" style={{ paddingBottom: 16 }}>
          <Link to="/studio" className="studio-btn-ghost" data-testid="topics-back" style={{ marginBottom: 12 }}>
            <ArrowLeft size={14} /> Back to Studio
          </Link>
          <span className="studio-eyebrow">
            <Sparkles size={12} /> AI Topic Suggestions
          </span>
          <h1 className="studio-h1">
            Your next 20 video ideas, <span className="studio-h1-accent">analysed from your channel.</span>
          </h1>
          <p className="studio-sub">
            We read your niche, target keywords and the last 30 published titles, then ask Claude Sonnet to
            recommend high-potential topics that fill content gaps and ride 2026 trends.
          </p>

          <div className="studio-hero-cta" style={{ flexWrap: "wrap", gap: 8 }}>
            <select
              className="studio-input"
              value={activeId}
              onChange={(e) => { setActiveId(e.target.value); setTopics([]); setMeta(null); }}
              data-testid="topics-channel-select"
              style={{ minWidth: 240 }}
            >
              <option value="">Select a channel…</option>
              {channels.map((c) => (
                <option key={c.channel_id} value={c.channel_id}>{c.title}</option>
              ))}
            </select>
            <button
              className="studio-btn-primary"
              onClick={generate}
              disabled={busy || !activeId}
              data-testid="topics-generate"
            >
              {busy ? <Loader2 size={16} className="ac-spin" /> : <Wand2 size={16} />}
              {topics.length ? "Regenerate" : "Generate topics"}
            </button>
            <button
              className="studio-btn-ghost"
              onClick={fetchTrending}
              disabled={trendBusy || !activeId}
              data-testid="topics-trending"
            >
              {trendBusy ? <Loader2 size={16} className="ac-spin" /> : <TrendingUp size={16} />}
              Trending keywords
            </button>
          </div>
        </div>

        {trending && trending.keywords?.length > 0 && (
          <div className="studio-card" style={{ padding: 16, marginBottom: 16 }} data-testid="trending-panel">
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <Flame size={16} style={{ color: "#ec7c5e" }} />
              <strong>Trending in {trending.niche}</strong>
              <small className="studio-muted" style={{ marginLeft: "auto" }}>{trending.region}</small>
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {trending.keywords.map((k, i) => (
                <span
                  key={i}
                  className={`studio-pill studio-pill--${k.trend === "rising" ? "warn" : k.trend === "hot" ? "bad" : "sm"}`}
                  data-testid={`trending-kw-${i}`}
                  title={k.why || ""}
                  style={{ cursor: "help" }}
                >
                  {k.trend === "rising" && <TrendingUp size={11} style={{ marginRight: 4 }} />}
                  {k.trend === "hot" && <Flame size={11} style={{ marginRight: 4 }} />}
                  {k.keyword}
                  <small style={{ marginLeft: 6, opacity: 0.7 }}>{k.volume_hint}</small>
                </span>
              ))}
            </div>
          </div>
        )}

        {busy && (
          <div className="studio-card" style={{ padding: 18, marginBottom: 14 }} data-testid="topics-progress">
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <Loader2 size={16} className="ac-spin" />
              <strong>Generating ideas with Gemini 3 Pro…</strong>
              <small className="studio-muted" style={{ marginLeft: "auto" }}>~10-20s · don&apos;t close this tab</small>
            </div>
            <div style={{ height: 6, background: "#E2E8F0", borderRadius: 4, overflow: "hidden" }}>
              <div
                style={{
                  width: "100%", height: "100%",
                  background: "linear-gradient(90deg, transparent, #a78bfa, transparent)",
                  animation: "ac-progress-stripe 1.6s linear infinite",
                  backgroundSize: "200% 100%",
                }}
              />
            </div>
            <ul style={{ marginTop: 12, padding: 0, listStyle: "none", fontSize: 12, color: "var(--st-text-soft)" }}>
              <li>📊 Pulling latest niche trends + your past hit titles…</li>
              <li>🧠 Cross-checking with channel memory (embeddings)…</li>
              <li>✨ Drafting 8-12 high-CTR formats…</li>
            </ul>
          </div>
        )}

        {error && error.niche_missing ? (
          <div className="studio-card" data-testid="topics-error" style={{ padding: 18, marginBottom: 14, border: "1px solid rgba(251,191,36,0.35)", background: "rgba(251,191,36,0.08)" }}>
            <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
              <AlertTriangle size={20} color="#F59E0B" style={{ marginTop: 2, flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <strong style={{ display: "block", marginBottom: 4 }}>Set your channel niche first</strong>
                <p style={{ margin: "0 0 12px", color: "#475569", fontSize: 14 }}>
                  {error.message}
                </p>
                <Link
                  to={error.fix_url}
                  className="studio-btn-primary"
                  data-testid="topics-fix-niche"
                  style={{ padding: "8px 14px", fontSize: 13 }}
                >
                  Set niche for {error.channel_title} →
                </Link>
              </div>
            </div>
          </div>
        ) : error && (
          <div className="ac-error" data-testid="topics-error">
            <AlertTriangle size={16} /> {typeof error === "string" ? error : JSON.stringify(error)}
          </div>
        )}

        {meta && (
          <div className="studio-card" style={{ padding: 14, marginBottom: 14 }} data-testid="topics-meta">
            <small className="studio-muted">
              Channel: <strong>{activeChannel?.title}</strong> · Niche: <strong>{meta.niche}</strong> ·
              Based on <strong>{meta.based_on}</strong> recent title(s)
            </small>
          </div>
        )}

        {!topics.length && !busy && (
          <div className="studio-empty" data-testid="topics-empty">
            <div className="studio-empty-icon"><Sparkles size={36} /></div>
            <h2>Generate your next batch of topics</h2>
            <p>Click <strong>Generate topics</strong> — we&apos;ll do the research.</p>
          </div>
        )}

        <div className="studio-grid" data-testid="topics-list">
          {topics.map((t, i) => (
            <motion.div
              key={i}
              className="studio-channel-card"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, delay: i * 0.03 }}
              data-testid={`topic-card-${i}`}
            >
              <div className="studio-channel-head" style={{ alignItems: "flex-start" }}>
                <div className="studio-channel-meta" style={{ flex: 1 }}>
                  <h3 style={{ fontSize: "1.05rem", lineHeight: 1.3 }}>{t.title}</h3>
                  <p style={{ marginTop: 4 }}>{t.angle}</p>
                </div>
                <span className={`studio-role studio-role--${t.format === "short" ? "designer" : "creator"}`}>
                  {t.format || "long"}
                </span>
              </div>
              <div style={{ padding: "8px 16px", fontSize: 13, color: "var(--ac-muted, #8b949e)" }}>
                <strong style={{ color: "var(--ac-fg, #e6edf3)" }}>Why it works:</strong> {t.why_it_works}
              </div>
              <div className="studio-channel-stats">
                <div className="studio-stat">
                  <span className="studio-stat-icon"><Sparkles size={14} /></span>
                  <div>
                    <strong style={{ fontSize: 13 }}>{t.primary_keyword || "—"}</strong>
                    <small>Primary keyword</small>
                  </div>
                </div>
                <div className="studio-stat">
                  <span className="studio-stat-icon"><ChevronRight size={14} /></span>
                  <div>
                    <strong style={{ fontSize: 13, textTransform: "capitalize" }}>
                      {t.estimated_search_intent || "informational"}
                    </strong>
                    <small>Intent</small>
                  </div>
                </div>
              </div>
              <div className="studio-channel-foot">
                <button
                  className={adding[i] === "done" ? "studio-btn-ghost" : "studio-btn-primary"}
                  onClick={() => addToPipeline(t, i)}
                  disabled={adding[i] === true || adding[i] === "done"}
                  data-testid={`topic-add-${i}`}
                  style={{ width: "100%" }}
                >
                  {adding[i] === true && <Loader2 size={14} className="ac-spin" />}
                  {adding[i] === "done" && <CheckCircle2 size={14} />}
                  {!adding[i] && <Plus size={14} />}
                  {adding[i] === "done" ? "Added to pipeline" : "Add to pipeline"}
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        {topics.length > 0 && (
          <div className="studio-section-h" style={{ marginTop: 24 }}>
            <button className="studio-btn-ghost" onClick={generate} disabled={busy} data-testid="topics-regen-bottom">
              <RefreshCw size={14} /> Regenerate
            </button>
          </div>
        )}
      </div>
      <Footer />
    </>
  );
}
